import { useState, useCallback } from "react";
import { View, Text, ScrollView, TouchableOpacity, RefreshControl, ActivityIndicator } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useFocusEffect, useRouter, useLocalSearchParams } from "expo-router";
import { getDogFollowers, type FollowerEntry } from "../../lib/api";
import { Ionicons } from "@expo/vector-icons";

export default function FollowersScreen() {
  const router = useRouter();
  const { dogId, dogName } = useLocalSearchParams<{ dogId: string; dogName?: string }>();
  const [followers, setFollowers] = useState<FollowerEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedFollower, setExpandedFollower] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!dogId) return;
    try {
      const list = await getDogFollowers(dogId);
      setFollowers(list);
    } catch {
      setFollowers([]);
    } finally {
      setLoading(false);
    }
  }, [dogId]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load])
  );

  async function onRefresh() {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }

  function goBack() {
    if (dogId) router.replace(`/(tabs)/dogs/${dogId}` as any);
    else router.replace("/(tabs)/dogs" as any);
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }} edges={["top"]}>
      {/* Header */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 12,
          paddingVertical: 12,
          backgroundColor: "#ffffff",
          borderBottomWidth: 1,
          borderBottomColor: "#e5e7eb",
        }}
      >
        <TouchableOpacity onPress={goBack} style={{ padding: 4, marginRight: 6 }}>
          <Ionicons name="chevron-back" size={24} color="#1B2A4A" />
        </TouchableOpacity>
        <Text style={{ fontSize: 18, fontWeight: "700", color: "#1B2A4A" }}>Followers</Text>
      </View>

      <ScrollView
        contentContainerStyle={{ padding: 16, paddingBottom: 40 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {/* Subtitle */}
        {dogName ? (
          <Text style={{ fontSize: 14, color: "#6b7280", marginBottom: 14 }}>
            People following <Text style={{ fontWeight: "700", color: "#1B2A4A" }}>{dogName}</Text>
          </Text>
        ) : null}

        {loading ? (
          <ActivityIndicator color="#1B2A4A" style={{ marginTop: 40 }} />
        ) : !followers || followers.length === 0 ? (
          <View style={{ alignItems: "center", marginTop: 50, paddingHorizontal: 24 }}>
            <View style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: "#FEF3C7", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <Ionicons name="people-outline" size={28} color="#F5A623" />
            </View>
            <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A", marginBottom: 6 }}>No followers yet</Text>
            <Text style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", lineHeight: 19 }}>
              Share your dog's public profile and handlers who follow it will show up here.
            </Text>
          </View>
        ) : (
          <View style={{ backgroundColor: "#fff", borderRadius: 12, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden" }}>
            {followers.map((f, idx) => {
              const expanded = expandedFollower === f.userId;
              const isLast = idx === followers.length - 1;
              return (
                <View key={f.userId} style={{ borderBottomWidth: isLast ? 0 : 1, borderBottomColor: "#f3f4f6" }}>
                  <TouchableOpacity
                    onPress={() => setExpandedFollower(expanded ? null : f.userId)}
                    style={{ flexDirection: "row", alignItems: "center", paddingHorizontal: 16, paddingVertical: 14, gap: 12 }}
                  >
                    <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: "#E5E7EB", alignItems: "center", justifyContent: "center" }}>
                      <Text style={{ fontWeight: "700", color: "#1B2A4A", fontSize: 16 }}>{(f.name[0] || "?").toUpperCase()}</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 15, fontWeight: "600", color: "#1B2A4A" }}>{f.name}</Text>
                      <Text style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                        {f.dogs.length === 0 ? "No dogs added yet" : `${f.dogs.length} ${f.dogs.length === 1 ? "dog" : "dogs"}`}
                      </Text>
                    </View>
                    {f.dogs.length > 0 && (
                      <Ionicons name={expanded ? "chevron-up" : "chevron-down"} size={18} color="#6b7280" />
                    )}
                  </TouchableOpacity>
                  {expanded && f.dogs.length > 0 && (
                    <View style={{ paddingHorizontal: 16, paddingBottom: 14, paddingTop: 2 }}>
                      {f.dogs.map((d) => {
                        const tappable = d.isPublic && d.slug;
                        return (
                          <TouchableOpacity
                            key={d.id}
                            disabled={!tappable}
                            onPress={() => {
                              if (!tappable) return;
                              // In-tab profile route (stays in the Dogs stack) so
                              // returning is a clean in-stack pop back to here.
                              router.push(
                                `/(tabs)/dogs/dogview/${d.slug}?backTo=followers&fromDogId=${dogId}&fromDogName=${encodeURIComponent(dogName || "")}` as any
                              );
                            }}
                            style={{ flexDirection: "row", alignItems: "center", paddingVertical: 7, opacity: tappable ? 1 : 0.5 }}
                          >
                            <Ionicons name="paw" size={14} color="#F5A623" style={{ marginRight: 8 }} />
                            <View style={{ flex: 1 }}>
                              <Text style={{ fontSize: 13, color: "#374151", fontWeight: "600" }}>{d.name}</Text>
                              <Text style={{ fontSize: 11, color: "#9ca3af" }}>{d.breed}{!d.isPublic ? " · private" : ""}</Text>
                            </View>
                            {tappable && <Ionicons name="chevron-forward" size={14} color="#d1d5db" />}
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
